// Floating layers for the New design: put an element next to an anchor inside the viewport (uiPlace) and show it in
// the browser's top layer (uiFloat), above every overflow, transform and stacking context. No side effects on import:
// ui-tip (tooltips, creator cards), ui-rows (row action bars) and uiDropdown (menus inside scrolling tables) share it.

const GAP = 8;
const EDGE = 8;

/**
 * Put a floating element next to an anchor, inside the viewport: on the preferred side ('top', 'bottom' or 'right')
 * when it fits, otherwise the other side, and slid sideways to stay on screen. The element must be position:fixed. Returns the side used.
 */
export function uiPlace(el, anchor, { side = 'top', align = 'center', gap = GAP } = {}) {
    const a = anchor.getBoundingClientRect();
    // measure at the left edge: where it last stood, the screen edge may have squeezed it narrower than it really is
    el.style.left = el.style.top = '0px';
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const vw = document.documentElement.clientWidth;
    const vh = window.innerHeight;
    // 'right': beside the anchor, vertically centred (the collapsed sidebar's icons); above it when there's no room
    if (side === 'right' && a.right + gap + w <= vw - EDGE) {
        el.style.left = `${Math.round(a.right + gap)}px`;
        el.style.top = `${Math.round(Math.max(EDGE, Math.min(vh - EDGE - h, a.top + a.height / 2 - h / 2)))}px`;
        el.dataset.side = 'right';
        el.style.setProperty('--ui-arrow', '0px');
        return 'right';
    }
    if (side === 'right') side = 'top';
    const above = a.top - gap - h;
    const below = a.bottom + gap;
    let used = side;
    if (side === 'top' && above < EDGE && below + h <= vh - EDGE) used = 'bottom';
    if (side === 'bottom' && below + h > vh - EDGE && above >= EDGE) used = 'top';
    const want = align === 'start' ? a.left : a.left + a.width / 2 - w / 2;
    const left = Math.max(EDGE, Math.min(vw - EDGE - w, want));
    el.style.left = `${Math.round(left)}px`;
    el.style.top = `${Math.round(used === 'top' ? above : below)}px`;
    el.dataset.side = used;
    // where the arrow points, measured from the bubble's left edge
    el.style.setProperty('--ui-arrow', `${Math.round(Math.max(12, Math.min(w - 12, a.left + a.width / 2 - left)))}px`);
    return used;
}

/** Show a floating element above everything: the top layer where the browser has it, a fixed layer otherwise. */
export function uiFloat(el) {
    if (!el.isConnected) document.body.appendChild(el);
    if (el.popover !== undefined && el.getAttribute('popover') === null) el.setAttribute('popover', 'manual');
    try { el.showPopover?.(); } catch (e) { /* already open */ }
    el.classList.add('ui-float-on');
}

export function uiUnfloat(el) {
    el.classList.remove('ui-float-on');
    try { el.hidePopover?.(); } catch (e) { /* already closed */ }
}
